import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { DataProviderInstance } from "../../../../api/DataProvider";
import { computeMeasurements, calculatePixelsPerMillimeter } from "../lib/cranialGeometry";
import { loadImagePixels, prepareCranialImage, revokePreparedImage } from "../lib/imagePreparation";
import { refineContour } from "../lib/contourRefinement";
import { validateCranialImage } from "../lib/imageValidation";
import { createLandmarksFromContour, createManualSeedGeometry } from "../lib/manualGeometry";

/**
 * Whole state of one cranial analysis (README 3.8). Lives in memory only:
 * nothing is persisted and reset() discards everything.
 */

const EMPTY_CALIBRATION = {
  status: "not_available",
  source: null,
  markerType: null,
  referenceSizeMm: null,
  referenceSizePx: null,
  pixelsPerMillimeter: null,
  confidence: null,
  points: [],
};

const INITIAL_STATE = {
  step: "source",
  sourceType: null,
  originalFile: null,
  image: null,
  isPreparing: false,
  validation: null,
  isAnalyzing: false,
  aiSuggestion: null,
  geometry: null,
  calibration: EMPTY_CALIBRATION,
  error: null,
};

const GENERIC_ANALYSIS_ERROR = "Não foi possível concluir a análise automática. Tente novamente ou marque manualmente.";

/**
 * Backend error codes (README 3.4) mapped to a pt-BR message and recovery options.
 * backTo: step the flow returns to when the image itself must change.
 */
const ERROR_DEFINITIONS = {
  NO_FILE: { message: "A foto não chegou ao servidor. Tente novamente.", canRetry: true, canTraceManually: true },
  UPLOAD_ERROR: { message: "Houve uma falha no envio da foto. Tente novamente.", canRetry: true, canTraceManually: true },
  DIMENSION_MISMATCH: { message: "Houve uma falha no envio da foto. Tente novamente.", canRetry: true, canTraceManually: true },
  INVALID_TYPE: { message: "Formato de imagem não aceito. Use uma foto JPEG, PNG ou WebP.", backTo: "source", canTraceManually: true },
  IMAGE_TOO_LARGE: { message: "A foto é grande demais para a análise. Escolha outra imagem.", backTo: "source", canTraceManually: true },
  IMAGE_TOO_SMALL: { message: "A resolução da foto é baixa demais para a análise. Use uma foto com mais resolução.", backTo: "source", canTraceManually: true },
  IMAGE_CORRUPT: { message: "Não foi possível ler esta foto. Escolha outra imagem.", backTo: "source" },
  AI_NOT_CONFIGURED: { message: "A análise automática não está disponível no momento. Você pode marcar o contorno manualmente.", canTraceManually: true },
  AI_TIMEOUT: { message: "A análise automática demorou mais que o esperado. Tente novamente ou marque manualmente.", canRetry: true, canTraceManually: true },
  AI_PROVIDER_ERROR: { message: "O serviço de análise automática está indisponível no momento. Tente novamente em instantes ou marque manualmente.", canRetry: true, canTraceManually: true },
  AI_REFUSED: { message: "A análise automática não pôde ser feita com esta foto. Marque manualmente ou tire outra foto.", backTo: "source", canTraceManually: true },
  AI_INVALID_RESPONSE: { message: "A análise automática retornou um resultado inválido. Tente novamente ou marque manualmente.", canRetry: true, canTraceManually: true },
  AI_LOW_CONFIDENCE: { message: "A análise automática teve baixa confiança nesta foto. Tente novamente, tire outra foto ou marque manualmente.", backTo: "source", canRetry: true, canTraceManually: true },
  IMAGE_UNUSABLE: { message: "Não foi possível identificar o contorno da cabeça nesta foto. Tire outra foto ou marque manualmente.", backTo: "source", canTraceManually: true },
  NETWORK_ERROR: { message: "Não foi possível se comunicar com o servidor. Verifique sua conexão e tente novamente.", canRetry: true, canTraceManually: true },
};

const buildAnalysisError = (code, serverMessage, warnings) => {
  const definition = ERROR_DEFINITIONS[code];
  return {
    code: code || "UNKNOWN",
    message: definition ? definition.message : serverMessage || GENERIC_ANALYSIS_ERROR,
    recoverable: true,
    backTo: (definition && definition.backTo) || "analysis",
    canRetry: definition ? Boolean(definition.canRetry) : true,
    canTraceManually: definition ? Boolean(definition.canTraceManually) : true,
    warnings: Array.isArray(warnings) ? warnings : [],
  };
};

// Edge refinement (README 3.7) runs on a clean downscale of the display copy.
const REFINEMENT_MAX_SIZE = 900;
// Hair or a hidden outline would pull the points to the wrong edge.
const REFINEMENT_SKIP_WARNINGS = ["HAIR_INTERFERENCE", "CONTOUR_HIDDEN"];

const warningCode = (warning) => (warning && typeof warning === "object" ? warning.code : warning);

const shouldRefine = (suggestion) =>
  Array.isArray(suggestion.cranialContour) &&
  suggestion.cranialContour.length >= 3 &&
  !(suggestion.warnings || []).some((warning) => REFINEMENT_SKIP_WARNINGS.indexOf(warningCode(warning)) !== -1);

/**
 * Snaps the AI geometry to the image edges. The refined geometry becomes the
 * suggestion itself (editor start and "Restaurar sugestão da IA"); the model
 * output is kept under rawGeometry. Never rejects: falls back to the raw geometry.
 */
const refineSuggestion = async (suggestion, display) => {
  const rawGeometry = { landmarks: suggestion.landmarks, cranialContour: suggestion.cranialContour };
  if (!shouldRefine(suggestion)) return { ...suggestion, rawGeometry, refinement: null };

  try {
    const { pixels, width, height } = await loadImagePixels(display.url, REFINEMENT_MAX_SIZE);
    const refined = refineContour({
      pixels,
      width,
      height,
      contour: suggestion.cranialContour,
      landmarks: suggestion.landmarks,
    });

    return {
      ...suggestion,
      // Only the contour is refined; the nose and ear landmarks stay as the model returned them.
      cranialContour: refined.contour,
      rawGeometry,
      refinement: refined.stats,
    };
  } catch (e) {
    console.warn("Cranial contour refinement failed; using the raw AI geometry.", e);
    return { ...suggestion, rawGeometry, refinement: null };
  }
};

const LANDMARK_KEYS = ["nose", "earLeft", "earRight"];

/**
 * Editor geometry from the AI suggestion (protocol v2 landmarks: nose, earLeft, earRight).
 * landmarks.posterior is left out, so the restored geometry uses the automatic P.
 * A landmark the response lacks is seeded from the contour so the editor always has
 * every handle to drag.
 */
const cloneGeometryFromSuggestion = (suggestion) => {
  const source = suggestion.landmarks || {};
  const fallback = createLandmarksFromContour(suggestion.cranialContour) || {};
  const landmarks = LANDMARK_KEYS.reduce((acc, key) => {
    const point = source[key] || fallback[key];
    if (point) acc[key] = { ...point };
    return acc;
  }, {});

  return {
    landmarks,
    cranialContour: suggestion.cranialContour.map((point) => ({ x: point.x, y: point.y })),
    wasManuallyEdited: false,
    source: "ai",
  };
};

/** Calibration prefilled from the AI marker suggestion, in display pixel space. */
const calibrationFromSuggestion = (suggestion, display) => {
  const hint = suggestion && suggestion.calibrationSuggestion;
  if (!hint || hint.status !== "detected") {
    return { ...EMPTY_CALIBRATION, status: "not_detected" };
  }

  const points = Array.isArray(hint.points) ? hint.points.map((point) => ({ x: point.x, y: point.y })) : [];
  const pixelsPerMillimeter = calculatePixelsPerMillimeter(points, hint.referenceSizeMm, display.width, display.height);

  return {
    status: "detected",
    source: "physical_marker",
    markerType: hint.markerType || null,
    referenceSizeMm: hint.referenceSizeMm || null,
    referenceSizePx: pixelsPerMillimeter ? pixelsPerMillimeter * hint.referenceSizeMm : null,
    pixelsPerMillimeter: pixelsPerMillimeter || null,
    confidence: typeof hint.confidence === "number" ? hint.confidence : null,
    points,
  };
};

export const isCalibrationValid = (calibration) =>
  Boolean(calibration) &&
  (calibration.status === "detected" || calibration.status === "manual") &&
  typeof calibration.pixelsPerMillimeter === "number" &&
  calibration.pixelsPerMillimeter > 0;

function reducer(state, action) {
  switch (action.type) {
    case "SET":
      return { ...state, ...action.payload };
    case "RESET":
      return INITIAL_STATE;
    default:
      return state;
  }
}

const useCranialAnalysis = () => {
  const { AnalyzeCranialImage } = DataProviderInstance();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  // Mirror of the latest state for async callbacks and unmount cleanup.
  const stateRef = useRef(state);
  stateRef.current = state;
  // Incremented on every async start/reset so stale results are ignored.
  const requestIdRef = useRef(0);

  const set = useCallback((payload) => dispatch({ type: "SET", payload }), []);

  useEffect(() => () => revokePreparedImage(stateRef.current.image), []);

  const { geometry, image, calibration } = state;

  const measurements = useMemo(() => {
    if (!geometry || !image) return null;

    return computeMeasurements({
      landmarks: geometry.landmarks,
      cranialContour: geometry.cranialContour,
      calibration: isCalibrationValid(calibration) ? calibration : null,
      imageWidth: image.display.width,
      imageHeight: image.display.height,
    });
  }, [geometry, image, calibration]);

  const replaceImage = useCallback((next) => {
    revokePreparedImage(stateRef.current.image);
    return next;
  }, []);

  const chooseSource = useCallback((sourceType) => {
    set({ sourceType, step: sourceType === "camera" ? "camera" : "source", error: null });
  }, [set]);

  const setCapturedFile = useCallback((file, sourceType) => {
    if (!file) return;
    const requestId = ++requestIdRef.current;
    set({ isPreparing: true, error: null, sourceType: sourceType || stateRef.current.sourceType });

    prepareCranialImage(file, { rotationDegrees: 0 })
      .then((prepared) => {
        if (requestId !== requestIdRef.current) {
          revokePreparedImage(prepared);
          return;
        }
        set({
          image: replaceImage(prepared),
          originalFile: file,
          isPreparing: false,
          validation: null,
          step: "preview",
        });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        set({
          isPreparing: false,
          step: "source",
          error: {
            code: "IMAGE_PREPARATION_FAILED",
            message: "Não foi possível abrir esta foto. Tente outra imagem.",
            recoverable: true,
            backTo: "source",
          },
        });
      });
  }, [set, replaceImage]);

  /** Rotates by +90 or -90 degrees, re-preparing from the original file. */
  const rotateImage = useCallback((deltaDegrees) => {
    const current = stateRef.current;
    if (!current.originalFile || !current.image || current.isPreparing) return;

    const requestId = ++requestIdRef.current;
    set({ isPreparing: true, error: null });

    prepareCranialImage(current.originalFile, { rotationDegrees: current.image.meta.rotationDegrees + deltaDegrees })
      .then((prepared) => {
        if (requestId !== requestIdRef.current) {
          revokePreparedImage(prepared);
          return;
        }
        set({ image: replaceImage(prepared), isPreparing: false, validation: null });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        set({
          isPreparing: false,
          error: { code: "IMAGE_ROTATION_FAILED", message: "Não foi possível girar a foto.", recoverable: true, backTo: "preview" },
        });
      });
  }, [set, replaceImage]);

  const usePhoto = useCallback(() => {
    if (!stateRef.current.image) return;
    set({ step: "validation", validation: null, error: null });
  }, [set]);

  /** Discards the current photo and goes back to the camera or the source choice. */
  const retake = useCallback((target) => {
    requestIdRef.current++;
    const current = stateRef.current;
    revokePreparedImage(current.image);
    const step = target || (current.sourceType === "camera" ? "camera" : "source");

    set({
      ...INITIAL_STATE,
      sourceType: step === "camera" ? "camera" : current.sourceType,
      step,
    });
  }, [set]);

  const validate = useCallback(() => {
    const current = stateRef.current;
    if (!current.originalFile || !current.image) return;
    const requestId = ++requestIdRef.current;

    validateCranialImage(current.originalFile, current.image.display)
      .then((validation) => {
        if (requestId !== requestIdRef.current) return;
        set({ validation });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        set({
          error: {
            code: "VALIDATION_FAILED",
            message: "Não foi possível verificar esta foto. Tente outra imagem.",
            recoverable: true,
            backTo: "source",
          },
        });
      });
  }, [set]);

  const analyze = useCallback(() => {
    const current = stateRef.current;
    if (!current.image) return;
    const requestId = ++requestIdRef.current;

    set({ step: "analysis", isAnalyzing: true, error: null, aiSuggestion: null, geometry: null });

    // CallPOSTAPIWithFormData parses the JSON body for every HTTP status, so
    // backend errors (4xx/5xx with { success:false, code }) arrive in the
    // success callback; errorCallback only fires on network/parse failures.
    AnalyzeCranialImage(
      {
        image: current.image.analysis.blob,
        analysisWidth: current.image.analysis.width,
        analysisHeight: current.image.analysis.height,
      },
      (response) => {
        if (requestId !== requestIdRef.current) return;
        const latest = stateRef.current;

        if (response && response.success && response.analysis) {
          // isAnalyzing stays true while the contour is refined.
          refineSuggestion(response.analysis, latest.image.display).then((suggestion) => {
            if (requestId !== requestIdRef.current) return;
            const current = stateRef.current;
            const validation = current.validation
              ? { ...current.validation, checks: { ...current.validation.checks, ...(suggestion.imageChecks || {}) } }
              : current.validation;

            set({
              isAnalyzing: false,
              aiSuggestion: suggestion,
              geometry: cloneGeometryFromSuggestion(suggestion),
              calibration: calibrationFromSuggestion(suggestion, current.image.display),
              validation,
            });
          });
          return;
        }

        set({
          isAnalyzing: false,
          error: buildAnalysisError(response && response.code, response && response.error, response && response.warnings),
        });
      },
      () => {
        if (requestId !== requestIdRef.current) return;
        set({ isAnalyzing: false, error: buildAnalysisError("NETWORK_ERROR") });
      }
    );
  }, [set, AnalyzeCranialImage]);

  const retryAnalysis = useCallback(() => analyze(), [analyze]);

  /** Goes to the editor after a successful AI suggestion. */
  const openEditor = useCallback(() => {
    if (!stateRef.current.geometry) return;
    set({ step: "editor", error: null });
  }, [set]);

  const startManualTracing = useCallback(() => {
    if (!stateRef.current.image) return;
    requestIdRef.current++;
    set({
      step: "editor",
      isAnalyzing: false,
      aiSuggestion: null,
      geometry: createManualSeedGeometry(),
      calibration: EMPTY_CALIBRATION,
      error: null,
    });
  }, [set]);

  const updateGeometry = useCallback((next) => {
    if (!next) return;
    // The editor owns the flag (undo back to the suggestion may clear it); default to edited.
    set({ geometry: { ...next, wasManuallyEdited: next.wasManuallyEdited !== false } });
  }, [set]);

  const updateCalibration = useCallback((next) => {
    if (!next) return;
    set({ calibration: { ...EMPTY_CALIBRATION, ...next } });
  }, [set]);

  const restoreSuggestion = useCallback(() => {
    const { aiSuggestion } = stateRef.current;
    set({ geometry: aiSuggestion ? cloneGeometryFromSuggestion(aiSuggestion) : createManualSeedGeometry() });
  }, [set]);

  const confirmMeasurement = useCallback(() => {
    if (!measurements || !measurements.ok) return;
    set({ step: "result", error: null });
  }, [set, measurements]);

  const backToEditor = useCallback(() => set({ step: "editor", error: null }), [set]);

  const clearError = useCallback(() => set({ error: null }), [set]);

  const reset = useCallback(() => {
    requestIdRef.current++;
    revokePreparedImage(stateRef.current.image);
    dispatch({ type: "RESET" });
  }, []);

  const actions = useMemo(() => ({
    chooseSource,
    setCapturedFile,
    rotateImage,
    usePhoto,
    retake,
    validate,
    analyze,
    retryAnalysis,
    openEditor,
    startManualTracing,
    updateGeometry,
    updateCalibration,
    restoreSuggestion,
    confirmMeasurement,
    backToEditor,
    clearError,
    reset,
  }), [
    chooseSource, setCapturedFile, rotateImage, usePhoto, retake, validate, analyze, retryAnalysis, openEditor,
    startManualTracing, updateGeometry, updateCalibration, restoreSuggestion, confirmMeasurement, backToEditor,
    clearError, reset,
  ]);

  return { state, measurements, actions };
};

export default useCranialAnalysis;
